// src/js/utils/feedTail.ts
// Phase 243 — dependency-free tail arithmetic for the `/mc/feed` live activity stream
// (D-02, D-03, D-06, D-07, D-08, D-14).
//
// This module exists because `src/js/mc/feed.ts` imports `highcharts` and
// `datatables.net-bs5` at module scope, so `node tests/mc/*.test.ts` could never load it
// without a bundler. Every function here is pure (no document, no window, no fetch, no
// setTimeout, no import) so that `tests/mc/feed-tail.test.ts` can pin it with plain
// `node`, no bundler and no jsdom — the same reason `utils/instants.ts` and
// `utils/mcKeys.ts` were extracted as zero-import modules before this one.
//
// ---------------------------------------------------------------------------------------
// RESEARCH.md Open Question 1 (cumulative growth over a long-lived tab) — RESOLVED here,
// a stated call rather than a silent choice.
// ---------------------------------------------------------------------------------------
// The three caps below (DEDUP_ID_CAP, PILL_QUEUE_CAP, STREAM_ROW_CAP) bound a long-lived
// (wall-mounted) tab:
//
//   - The dedup id Set only has to absorb SAME-SECOND re-delivery at the inclusive `>=`
//     tail boundary (D-06). An id whose row is older than the current watermark second
//     can never be re-delivered by a later tick, so 500 is far above any realistic
//     same-second burst on this fleet.
//   - The DOM-row cap and the pill-queue cap do NOT evict rows from the middle or the
//     tail of the list. Evicting the OLDEST rows to make room would open exactly the
//     silent mid-list hole D-08 exists to prevent. Instead, an over-cap arrival routes
//     into D-08's already-defined page-0 reset via shouldResetToPage0() below.
//
// The result: this phase has ONE recovery path (a fresh page-0 replaceChildren()) shared
// by D-08 tail overflow, D-14 zero-baseline, and cap exhaustion — not three different
// recovery mechanisms to reason about.

/** Mirrors `McFeedModel::getActivityStream()`'s own `int $limit = 25` default — never a
 *  second, independently-chosen page size. */
export const FEED_PAGE_LIMIT = 25;

/** RESEARCH Open Question 1 cap — see header comment above. Oldest-first eviction lives
 *  in recordSeenIds(). */
export const DEDUP_ID_CAP = 500;

/** RESEARCH Open Question 1 cap — an over-cap pill queue routes into shouldResetToPage0(),
 *  it is never trimmed from the middle. */
export const PILL_QUEUE_CAP = 100;

/** RESEARCH Open Question 1 cap — an over-cap in-DOM row count routes into
 *  shouldResetToPage0(), it is never trimmed from the middle or the tail. */
export const STREAM_ROW_CAP = 300;

/** The structural subset of `feed.ts`'s `StreamRow` these functions need. */
export interface TailRowLike {
    id: string;
    event_type: string;
    badge: string;
    occurred_at_ms: number;
}

/**
 * D-14: when the caller holds no baseline instant, the tick must do a normal page-0 fetch,
 * not a tail. Returns `null` when `newestOccurredAtMs` is `null` or not a finite number;
 * otherwise returns the ISO instant string the `since` query param carries.
 */
export function tailSinceParam(newestOccurredAtMs: number | null): string | null {
    if (newestOccurredAtMs === null || !Number.isFinite(newestOccurredAtMs)) {
        return null;
    }
    return new Date(newestOccurredAtMs).toISOString();
}

/**
 * D-06: the tail boundary is inclusive (`>=`) because `mc_events.occurred_at` is a
 * second-granularity `DATETIME` and `mc_events.id` is a non-monotonic, mod-generated UUID
 * (sql/227-migration.sql:101-113) — two events in the same second are entirely ordinary
 * (a join burst, a mass death), so a strict `>` boundary would silently lose a same-second
 * sibling that lands after the tick. The watermark second is therefore deliberately
 * re-queried every tick, and THIS function is what makes that safe: it drops rows whose
 * `id` is already in `seen` (cross-batch) or already emitted earlier in the SAME call
 * (intra-batch — a same-second sibling delivered twice in one response). The dedup set is
 * NOT redundant with `>=`; it is the reason `>=` is correct.
 *
 * Preserves input order. Does not mutate `seen`.
 */
export function selectUnseenRows<T extends { id: string }>(rows: readonly T[], seen: ReadonlySet<string>): T[] {
    const emittedThisCall = new Set<string>();
    const result: T[] = [];
    for (const row of rows) {
        if (seen.has(row.id) || emittedThisCall.has(row.id)) {
            continue;
        }
        emittedThisCall.add(row.id);
        result.push(row);
    }
    return result;
}

/**
 * Inserts every id into `seen`, then evicts oldest-first (a `Set` iterates in insertion
 * order, so `seen.values().next().value` is always the oldest surviving entry) while the
 * set exceeds `cap`. See the RESEARCH Open Question 1 header comment for why oldest-first
 * eviction here is safe and does not risk re-rendering a duplicate row.
 */
export function recordSeenIds(ids: readonly string[], seen: Set<string>, cap: number = DEDUP_ID_CAP): void {
    for (const id of ids) {
        seen.add(id);
    }
    while (seen.size > cap) {
        const oldest = seen.values().next().value;
        if (oldest === undefined) break;
        seen.delete(oldest);
    }
}

/**
 * Returns the maximum of `current` and every finite `occurred_at_ms` across `rows`.
 * Returns `current` unchanged for an empty array. Never regresses to an older instant —
 * a late-arriving older row cannot rewind the tail baseline.
 */
export function advanceWatermark(rows: readonly { occurred_at_ms: number }[], current: number | null): number | null {
    let max = current;
    for (const row of rows) {
        if (!Number.isFinite(row.occurred_at_ms)) continue;
        if (max === null || row.occurred_at_ms > max) {
            max = row.occurred_at_ms;
        }
    }
    return max;
}

/**
 * D-02/D-03's type-aware refetch trigger. Empty array → all three false, expressing D-02's
 * "nothing new arrived, so zero extra requests" rule as data.
 *
 * Two accepted blind spots, recorded here because a reviewer will otherwise file them as
 * bugs:
 *
 * 1. D-03's OWN stated blind spot — the three aggregate signals (getKd/getRarity/getFirsts)
 *    take only `$server` and are all-time, while the stream is additionally range- and
 *    type-filtered. With the type filter on "Deaths", an advancement that just changed
 *    Rarity/Firsts will not trigger their refresh until the filter widens or the page
 *    reloads. Accepted, not fixed; the escalation path is CONTEXT.md's deferred
 *    "filter-independent freshness probe".
 *
 * 2. A wider consequence discovered during planning, recorded rather than silently patched
 *    — `McFeedModel::ACTIVITY_EVENT_TYPES` is exactly
 *    `['death', 'kill', 'session.join', 'session.quit']`, so `advancement`/`first` rows
 *    cannot reach the activity stream at all today. The rarity/firsts branch below is
 *    therefore correct-but-currently-unreachable: on today's data a poll tick refreshes the
 *    stream and K/D, and Rarity/Firsts refresh only on a filter change or reload. Do NOT
 *    "fix" this by widening the trigger to any-new-row — D-03 explicitly rejected that —
 *    and do NOT delete the branch; adding `advancement` to `ACTIVITY_EVENT_TYPES` is a
 *    separate decision about what the stream shows, which this phase's own scope forbids.
 */
export function aggregateRefetchTargets(
    rows: readonly { event_type: string; badge: string }[],
): { kd: boolean; rarity: boolean; firsts: boolean } {
    let kd = false;
    let rarity = false;
    let firsts = false;
    for (const row of rows) {
        if (row.event_type === 'death' || row.event_type === 'kill') {
            kd = true;
        }
        if (row.event_type === 'advancement' || row.badge === 'first') {
            rarity = true;
            firsts = true;
        }
    }
    return { kd, rarity, firsts };
}

/**
 * D-07: "Load more" offset drift correction. Prepending K rows shifts the server's
 * positional window down by K, so the client tracks K since load and sends `offset + K`.
 * Returns `null` when `serverNextOffset` is `null` (no next page). A negative
 * `compensation` is clamped to zero rather than trusted.
 */
export function compensatedOffset(serverNextOffset: number | null, compensation: number): number | null {
    if (serverNextOffset === null) return null;
    return serverNextOffset + Math.max(0, compensation);
}

/**
 * D-08 (extended by RESEARCH Open Question 1's cap resolution above): returns true when the
 * server flagged tail overflow, when the pill queue passed PILL_QUEUE_CAP while the viewer
 * was scrolled away, or when the incoming batch would push the in-DOM row count past
 * STREAM_ROW_CAP. Every true branch here routes to the SAME page-0 recovery
 * (`replaceChildren()` a fresh page 0) — prepending a partial batch instead would leave a
 * silent hole in the middle of the list that the viewer can neither see nor scroll to.
 */
export function shouldResetToPage0(input: {
    overflowed: boolean;
    domRowCount: number;
    incomingCount: number;
    queueResetPending: boolean;
}): boolean {
    if (input.overflowed) return true;
    if (input.queueResetPending) return true;
    return input.domRowCount + input.incomingCount > STREAM_ROW_CAP;
}

/**
 * The locked copy from 243-UI-SPEC's Copywriting Contract, used VERBATIM for both the
 * arrival pill label and the count-only aria-live announcement. No synonyms, no "+" suffix,
 * no other noun.
 */
export function arrivalLabel(count: number): string {
    return count === 1 ? '1 new event' : `${count} new events`;
}
